import { RefObject } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNotification, useTranslate } from "@refinedev/core";

import { ModalRef } from "@shared/components";
import { useEditBookActivityMutation } from "@modules/bookActivities/services/mutations";
import { BookActivityFormValues, bookActivitySchema } from "@modules/bookActivities/validations";
import { listBookActivitiesQueryKey } from "@modules/bookActivities/services/queries";

export function useEditBookActivityModal(modalRef: RefObject<ModalRef>, onComplete: () => void) {
  const queryClient = useQueryClient();
  const translate = useTranslate();
  const { open } = useNotification();

  const methods = useForm<BookActivityFormValues>({
    resolver: zodResolver(bookActivitySchema()),
    mode: "onBlur",
  });

  const { mutateAsync, isPending } = useEditBookActivityMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listBookActivitiesQueryKey,
        refetchType: 'all',
      });

      open?.({
        type: "success",
        message: translate("book_activity_admin.success.edit"),
      });

      onComplete();
      modalRef.current?.close();
    },
  });

  const handleSubmit = async (data: BookActivityFormValues) => {
    const activityId = modalRef.current?.getId();
    await mutateAsync({ id: activityId as string, data });
  }

  return {
    methods,
    isPending,
    handleSubmit,
  };
}
